import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getEvents = createAsyncThunk(
    'events/getEvents',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.EVENTS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Events retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch events',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch events';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch events';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getEvent = createAsyncThunk(
    'events/getEvent',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.EVENT(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Event retrieved successfully',
                    event: data?.event || null,
                    statistics: data?.statistics || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch event',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch event';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch event';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createEvent = createAsyncThunk(
    'events/createEvent',
    async (eventData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.EVENT_CREATE, eventData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Event created successfully',
                    event: data?.event || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create event',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to create event';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create event';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateEvent = createAsyncThunk(
    'events/updateEvent',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.EVENT_UPDATE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Event updated successfully',
                    event: responseData?.event || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update event',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update event';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update event';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteEvent = createAsyncThunk(
    'events/deleteEvent',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.EVENT_DELETE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Event deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete event',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete event';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete event';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const duplicateEvent = createAsyncThunk(
    'events/duplicateEvent',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.EVENT_DUPLICATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Event duplicated successfully',
                    event: data?.event || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to duplicate event',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to duplicate event';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to duplicate event';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendEventInvites = createAsyncThunk(
    'events/sendInvites',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.EVENT_SEND_INVITES(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Invites sent successfully',
                    result: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send invites',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to send invites';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send invites';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendEventReminders = createAsyncThunk(
    'events/sendReminders',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.EVENT_SEND_REMINDERS(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Reminders sent successfully',
                    result: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send reminders',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to send reminders';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send reminders';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const completeEvent = createAsyncThunk(
    'events/completeEvent',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.EVENT_COMPLETE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Event completed successfully',
                    event: data?.event || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to complete event',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to complete event';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to complete event';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getEventRsvps = createAsyncThunk(
    'events/getRsvps',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.EVENT_RSVPS(id), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'RSVPs retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch RSVPs',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch RSVPs';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch RSVPs';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const submitEventRsvp = createAsyncThunk(
    'events/submitRsvp',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.EVENT_RSVPS(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'RSVP submitted successfully',
                    rsvp: responseData?.rsvp || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to submit RSVP',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to submit RSVP';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to submit RSVP';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getEventStats = createAsyncThunk(
    'events/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.EVENT_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Event statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch event statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch event statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch event statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportEvents = createAsyncThunk(
    'events/export',
    async ({ type, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.EVENT_EXPORT(type), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    events: [],
    currentEvent: null,
    rsvps: [],
    stats: {
        total_events: 0,
        upcoming_events: 0,
        completed_events: 0,
        draft_events: 0,
        recurring_events: 0,
        total_rsvp_yes: 0,
        total_rsvp_no: 0,
        total_sent: 0,
        avg_rsvp_rate: 0,
        events_by_month: {},
        stats: {
            total: 0,
            upcoming: 0,
            completed: 0,
            draft: 0,
            recurring: 0,
            rsvp_yes: 0,
            rsvp_no: 0,
            total_sent: 0,
            avg_rsvp_rate: 0
        }
    },
    exportData: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// ============================================
// SLICE
// ============================================

const eventSlice = createSlice({
    name: 'events',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearEventState: (state) => {
            state.events = [];
            state.currentEvent = null;
            state.rsvps = [];
            state.stats = initialState.stats;
            state.exportData = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentEvent: (state) => {
            state.currentEvent = null;
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
        clearRsvps: (state) => {
            state.rsvps = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET EVENTS =====
            .addCase(getEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.events = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.stats = { ...state.stats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Events retrieved successfully';
                state.apiMessage = payload.message || 'Events retrieved successfully';
                state.error = null;
            })
            .addCase(getEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch events';
                state.apiMessage = action.payload?.message || 'Failed to fetch events';
                state.successMessage = null;
                state.events = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET EVENT =====
            .addCase(getEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentEvent = payload.event || null;
                state.successMessage = payload.message || 'Event retrieved successfully';
                state.apiMessage = payload.message || 'Event retrieved successfully';
                state.error = null;
            })
            .addCase(getEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch event';
                state.apiMessage = action.payload?.message || 'Failed to fetch event';
                state.successMessage = null;
                state.currentEvent = null;
            })

            // ===== CREATE EVENT =====
            .addCase(createEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Event created successfully';
                state.apiMessage = payload.message || 'Event created successfully';
                state.error = null;
                if (payload.event) {
                    state.events = [payload.event, ...state.events];
                }
            })
            .addCase(createEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create event';
                state.apiMessage = action.payload?.message || 'Failed to create event';
                state.successMessage = null;
            })

            // ===== UPDATE EVENT =====
            .addCase(updateEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Event updated successfully';
                state.apiMessage = payload.message || 'Event updated successfully';
                state.error = null;
                if (payload.event) {
                    state.events = state.events.map(e => 
                        e.id === payload.event.id ? payload.event : e
                    );
                    if (state.currentEvent?.id === payload.event.id) {
                        state.currentEvent = payload.event;
                    }
                }
            })
            .addCase(updateEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update event';
                state.apiMessage = action.payload?.message || 'Failed to update event';
                state.successMessage = null;
            })

            // ===== DELETE EVENT =====
            .addCase(deleteEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Event deleted successfully';
                state.apiMessage = payload.message || 'Event deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.events = state.events.filter(e => e.id !== payload.id);
                    if (state.currentEvent?.id === payload.id) {
                        state.currentEvent = null;
                    }
                }
            })
            .addCase(deleteEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete event';
                state.apiMessage = action.payload?.message || 'Failed to delete event';
                state.successMessage = null;
            })

            // ===== DUPLICATE EVENT =====
            .addCase(duplicateEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(duplicateEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Event duplicated successfully';
                state.apiMessage = payload.message || 'Event duplicated successfully';
                state.error = null;
                if (payload.event) {
                    state.events = [payload.event, ...state.events];
                }
            })
            .addCase(duplicateEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate event';
                state.apiMessage = action.payload?.message || 'Failed to duplicate event';
                state.successMessage = null;
            })

            // ===== SEND INVITES =====
            .addCase(sendEventInvites.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendEventInvites.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Invites sent successfully';
                state.apiMessage = payload.message || 'Invites sent successfully';
                state.error = null;
                // Update stats
                if (payload.result?.sent && state.stats) {
                    state.stats.total_sent = (state.stats.total_sent || 0) + payload.result.sent;
                }
            })
            .addCase(sendEventInvites.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send invites';
                state.apiMessage = action.payload?.message || 'Failed to send invites';
                state.successMessage = null;
            })

            // ===== SEND REMINDERS =====
            .addCase(sendEventReminders.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendEventReminders.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Reminders sent successfully';
                state.apiMessage = payload.message || 'Reminders sent successfully';
                state.error = null;
            })
            .addCase(sendEventReminders.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send reminders';
                state.apiMessage = action.payload?.message || 'Failed to send reminders';
                state.successMessage = null;
            })

            // ===== COMPLETE EVENT =====
            .addCase(completeEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(completeEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Event completed successfully';
                state.apiMessage = payload.message || 'Event completed successfully';
                state.error = null;
                if (payload.event) {
                    state.events = state.events.map(e => 
                        e.id === payload.event.id ? payload.event : e
                    );
                    if (state.currentEvent?.id === payload.event.id) {
                        state.currentEvent = payload.event;
                    }
                }
            })
            .addCase(completeEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to complete event';
                state.apiMessage = action.payload?.message || 'Failed to complete event';
                state.successMessage = null;
            })

            // ===== GET RSVPS =====
            .addCase(getEventRsvps.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getEventRsvps.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.rsvps = responseData.data || [];
                state.successMessage = payload.message || 'RSVPs retrieved successfully';
                state.apiMessage = payload.message || 'RSVPs retrieved successfully';
                state.error = null;
            })
            .addCase(getEventRsvps.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch RSVPs';
                state.apiMessage = action.payload?.message || 'Failed to fetch RSVPs';
                state.successMessage = null;
                state.rsvps = [];
            })

            // ===== SUBMIT RSVP =====
            .addCase(submitEventRsvp.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(submitEventRsvp.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'RSVP submitted successfully';
                state.apiMessage = payload.message || 'RSVP submitted successfully';
                state.error = null;
                if (payload.rsvp) {
                    state.rsvps = [payload.rsvp, ...state.rsvps];
                }
            })
            .addCase(submitEventRsvp.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to submit RSVP';
                state.apiMessage = action.payload?.message || 'Failed to submit RSVP';
                state.successMessage = null;
            })

            // ===== GET STATS =====
            .addCase(getEventStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getEventStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = { ...state.stats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Event statistics retrieved successfully';
                state.apiMessage = payload.message || 'Event statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getEventStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch event statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch event statistics';
                state.successMessage = null;
            })

            // ===== EXPORT =====
            .addCase(exportEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearEventState,
    clearCurrentEvent,
    clearExportData,
    clearRsvps,
} = eventSlice.actions;

export const selectEvents = (state) => state.events?.events || [];
export const selectCurrentEvent = (state) => state.events?.currentEvent || null;
export const selectEventRsvps = (state) => state.events?.rsvps || [];
export const selectEventStats = (state) => state.events?.stats || initialState.stats;
export const selectEventPagination = (state) => state.events?.pagination || initialState.pagination;
export const selectEventLoading = (state) => state.events?.isLoading || false;
export const selectEventError = (state) => state.events?.error || null;
export const selectEventSuccess = (state) => state.events?.successMessage || null;
export const selectEventApiMessage = (state) => state.events?.apiMessage || null;
export const selectEventExportData = (state) => state.events?.exportData || null;

export const selectEventStatsSummary = (state) => {
    const stats = selectEventStats(state);
    return {
        totalEvents: stats.total_events || 0,
        upcomingEvents: stats.upcoming_events || 0,
        completedEvents: stats.completed_events || 0,
        draftEvents: stats.draft_events || 0,
        recurringEvents: stats.recurring_events || 0,
        totalRsvpYes: stats.total_rsvp_yes || 0,
        totalRsvpNo: stats.total_rsvp_no || 0,
        totalSent: stats.total_sent || 0,
        avgRsvpRate: stats.avg_rsvp_rate || 0,
        eventsByMonth: stats.events_by_month || {},
        stats: stats.stats || {}
    };
};

export default eventSlice.reducer;